import { useState } from 'react';
import { PageSEO } from '@/components/base/PageSEO';
import { Breadcrumb } from '@/components/feature/Breadcrumb';
import { aboutData, faqList } from '@/mocks/about';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '一起草',
  url: 'https://cswan771.cn',
  description: '一起草是一个AI智能内容创作平台，提供AI写作、文章润色、大纲生成等一站式内容创作解决方案。',
  foundingDate: '2024',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '400-888-9999',
    email: 'support@yiqicao.com',
    contactType: 'customer service',
    availableLanguage: ['Chinese'],
  },
  address: {
    '@type': 'PostalAddress',
    streetAddress: '中关村科技园区创新路88号',
    addressLocality: '北京',
    addressRegion: '海淀区',
    addressCountry: 'CN',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqList.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于一起草',
  description: '一起草品牌介绍、发展历程、联系方式',
  url: 'https://cswan771.cn/about',
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');

    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl?.value.trim()) {
      setFormStatus('success');
      setSubmitting(false);
      return;
    }

    try {
      const formData = new FormData(form);
      const res = await fetch(form.getAttribute('action')!, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试';
        if (msg.toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后再试');
        } else {
          setFormError(msg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageSEO
        title="关于我们"
        description="一起草品牌介绍、发展历程、核心价值与联系方式。了解一起草如何用AI技术赋能内容创作，为超50万用户提供智能创作服务。"
        keywords="一起草关于我们,一起草品牌,一起草联系方式,一起草官网入口"
        canonicalUrl="/about"
        schema={[organizationSchema, faqSchema, aboutSchema]}
      />

      <main className="pt-20 md:pt-24 pb-16 md:pb-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          <section className="mb-16">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
              关于一起草
            </h1>
            <p className="text-foreground-600 leading-relaxed max-w-3xl text-sm md:text-base">
              {aboutData.introduction}
            </p>
          </section>

          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-eye-line text-primary-600 text-lg"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-2 text-sm">愿景</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutData.vision}</p>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                <div className="w-10 h-10 bg-accent-50 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-flag-line text-accent-600 text-lg"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-2 text-sm">使命</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutData.mission}</p>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                <div className="w-10 h-10 bg-secondary-50 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-heart-line text-secondary-600 text-lg"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-2 text-sm">价值观</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">用户至上、技术创新、内容质量、开放共赢</p>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-6 top-2 bottom-2 w-px bg-background-200"></div>
              <div className="space-y-6">
                {aboutData.milestones.map((m, i) => (
                  <div key={i} className="relative flex gap-4 md:gap-6 pl-10 md:pl-14">
                    <div className="absolute left-2 md:left-3.5 top-1.5 w-4 h-4 bg-primary-500 rounded-full border-2 border-background-50 z-10"></div>
                    <div className="flex-1 bg-background-50 border border-background-200/70 rounded-xl p-5">
                      <span className="text-xs text-primary-600 font-medium">{m.year}</span>
                      <h3 className="font-heading font-semibold text-foreground-900 text-sm mt-1 mb-1">{m.title}</h3>
                      <p className="text-sm text-foreground-600">{m.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
              核心优势
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {aboutData.values.map((v) => (
                <div key={v.title} className="bg-background-50 border border-background-200/70 rounded-xl p-5 text-center">
                  <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-2">{v.title}</h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-600"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-foreground-900 mb-1">电子邮箱</h4>
                    <p className="text-sm text-foreground-600">{aboutData.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-accent-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-accent-600"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-foreground-900 mb-1">客服热线</h4>
                    <p className="text-sm text-foreground-600">{aboutData.contact.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-secondary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-secondary-600"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-foreground-900 mb-1">公司地址</h4>
                    <p className="text-sm text-foreground-600">{aboutData.contact.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-background-200 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-foreground-500"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-foreground-900 mb-1">工作时间</h4>
                    <p className="text-sm text-foreground-600">{aboutData.contact.workingHours}</p>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                <h3 className="font-heading font-semibold text-foreground-900 mb-4 text-base">发送消息</h3>
                <form
                  data-readdy-form
                  action="https://readdy.ai/api/form/d9u0v0tca23r0tr1aoa0"
                  method="POST"
                  onSubmit={handleSubmit}
                >
                  <input type="text" name="phone_alt" className="honeypot-field" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                        <input
                          type="text"
                          id="contact-name"
                          name="name"
                          required
                          className="w-full px-3.5 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                        <input
                          type="email"
                          id="contact-email"
                          name="email"
                          required
                          className="w-full px-3.5 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入邮箱"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                      <input
                        type="text"
                        id="contact-subject"
                        name="subject"
                        required
                        className="w-full px-3.5 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入主题"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">消息</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        className="w-full px-3.5 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                        placeholder="请输入您的消息（最多500字）"
                      ></textarea>
                    </div>

                    {formError && (
                      <p className="text-sm text-red-600">{formError}</p>
                    )}
                    {formStatus === 'success' && !formError && (
                      <p className="text-sm text-green-600">消息已成功发送，我们会尽快回复您！</p>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                    >
                      {submitting ? '发送中...' : '发送消息'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}